// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, SchemaForm } from "@components";
import actionCreators from "@modules/reporting/actionCreator";
import {
  showStatusReferralDetailsModal,
  selectedStatusReferralDetails,
  selectClosedReferralsStatuses,
  selectReferralCommonData,
} from "@modules/reporting/selectors/referral-tracker";
import type {
  CommonData,
  ReferralStatusDetails,
} from "@modules/reporting/reducer/referral-tracker/types";

type OwnProps = $Exact<{
  data: number,
}>;

type PropsFromState = $Exact<{
  showModal: boolean,
  details: ReferralStatusDetails,
  closedReferralsStatuses: string[],
  commonData: CommonData,
}>;

type PropsFromDispatch = $Exact<{
  toggleStatusReferralDetailsModal: typeof actionCreators.ToggleStatusReferralDetailsModal,
  submitSupplierReferralStatus: typeof actionCreators.SubmitSupplierReferralStatus,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class ReferralStatusModal extends Component<Props> {
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }

  handleCancel = () => {
    this.props.toggleStatusReferralDetailsModal();
  };

  submitForm = () => {
    if (this.formRef) {
      this.formRef.current.submit();
    }
  };
  handleSubmit = (formData: Object) => {
    this.props.submitSupplierReferralStatus({
      clientId: this.props.commonData.clientId,
      supplierReferralId: this.props.commonData.referralId,
      supplierReferralStatus: formData.status,
    });
  };

  get schema() {
    return {
      required: ["status"],
      type: "object",
      properties: {
        status: {
          title: this.props.t("TREATMENT_OUTCOME"),
          type: "string",
          oneOf: this.props.closedReferralsStatuses.map(itm => ({
            title: this.props.t(itm),
            const: itm,
          })),
        },
      },
    };
  }
  get uiSchema() {
    return {
      status: {
        "ui:options": {
          hidelabel: true,
        },
      },
    };
  }

  render() {
    if (!this.props.showModal) {
      return null;
    }

    return (
      <Modal className="modal-normal">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("REFERRAL_STATUS")} #{this.props.commonData.referralId}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center h-300px">
          <p>{this.props.t("REFERRAL_STATUS_MODAL_MESSAGE")}:</p>
          <SchemaForm
            className="w-100"
            ref={this.formRef}
            schema={this.schema}
            uiSchema={this.uiSchema}
            onSubmit={this.handleSubmit}
            externalSubmit
          />
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CLOSE")}
          </Button>
          <Button materialIcon="replay_circle_filled" onClick={this.submitForm}>
            {this.props.t("SUBMIT")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    showModal: showStatusReferralDetailsModal(state),
    details: selectedStatusReferralDetails(state),
    closedReferralsStatuses: selectClosedReferralsStatuses(state),
    commonData: selectReferralCommonData(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  toggleStatusReferralDetailsModal: actionCreators.ToggleStatusReferralDetailsModal,
  submitSupplierReferralStatus: actionCreators.SubmitSupplierReferralStatus,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(ReferralStatusModal);
